import type { ApiDashboardElement } from '@highimpact-frontend/api-types';
import type {
  DisplayerWidgetType,
  EmptyInitialPlotType,
  EmptyInitialResourceWidgetType,
  EmptyInitialTableType,
  ImageGalleryWidgetType,
  RemoteWidgetType,
  TextType,
  WidgetNameType,
  WidgetParamsFromName,
  WidgetType,
} from '@highimpact/dashboard-types';

import ImageGallery from '../../dashboard/widget/widgets/imageGallery/imageGallery/ImageGallery';
import ImageWidget from '../../dashboard/widget/widgets/image/image/ImageWidget';
import Map from '../../dashboard/widget/widgets/map/Map';
import RemoteWidget from '../../dashboard/widget/widgets/remoteWidget/RemoteWidget';
import TextFromExternal from '../../dashboard/widget/widgets/textFromProperty/TextFromExternal';
import Text from '../../dashboard/widget/widgets/text/Text';
import DisplayerWidget from '../../dashboard/widget/widgets/displayerWidget/DisplayerWidget';
import TextWidget from '../../dashboard/widget/widgets/textFromProperty/TextFromPropertyDisplayer';
import GroupWidget from '../../dashboard/widget/widgets/groupWidget/GroupWidget';
import EmptyInitialPlot from '../../dashboard/widget/widgets/plots/EmptyInitialPlot';
import EmptyInitialTable from '../../dashboard/widget/widgets/tables/EmptyInitialTable';
import EmptyInitialResourceWidget from '../../dashboard/widget/widgets/resources/EmptyInitialResourceWidget';

export type {
  DisplayerWidgetType,
  EmptyInitialPlotType,
  EmptyInitialResourceWidgetType,
  EmptyInitialTableType,
  ImageGalleryWidgetType,
  RemoteWidgetType,
  TextType,
  WidgetNameType,
  WidgetParamsFromName,
  WidgetType,
} from '@highimpact/dashboard-types';

export {
  isDisplayer,
  isGroup,
  isImageGalleryWidget,
  isImageWidget,
  isMapWidget,
  isRemoteWidget,
  isText,
  isTextFromExternal,
  isTextInput,
} from '@highimpact/dashboard-types';

/**
 * Get a widget component type from its code name.
 * Kept in the dashboard lib because it depends on React components.
 */
export type WidgetFromName<T extends WidgetNameType> = T extends 'displayer'
  ? typeof DisplayerWidget
  : T extends 'empty-initial-plot'
  ? typeof EmptyInitialPlot
  : T extends 'empty-initial-table'
  ? typeof EmptyInitialTable
  : T extends 'empty-initial-resource-widget'
  ? typeof EmptyInitialResourceWidget
  : T extends 'text'
  ? typeof TextWidget
  : T extends 'text-input'
  ? typeof Text
  : T extends 'text-from-external'
  ? typeof TextFromExternal
  : T extends 'group'
  ? typeof GroupWidget
  : T extends 'mapWidget'
  ? typeof Map
  : T extends 'imageWidget'
  ? typeof ImageWidget
  : T extends 'remote-widget'
  ? typeof RemoteWidget
  : T extends 'image-gallery-widget'
  ? typeof ImageGallery
  : never;

export const makeDisplayerRepresentation = (
  dashboardElementId: number,
  elementData: ApiDashboardElement,
  props: WidgetParamsFromName<'displayer'>
): DisplayerWidgetType => ({
  type: 'displayer',
  dashboardElementId,
  elementData,
  props,
});

export const makeEmptyInitialPlotRepresentation = (
  dashboardElementId: number,
  elementData: ApiDashboardElement,
  props: WidgetParamsFromName<'empty-initial-plot'>
): EmptyInitialPlotType => ({
  type: 'empty-initial-plot',
  dashboardElementId: dashboardElementId,
  elementData: elementData,
  props: props,
});

export const makeEmptyInitialTableRepresentation = (
  dashboardElementId: number,
  elementData: ApiDashboardElement,
  props: WidgetParamsFromName<'empty-initial-table'>
): EmptyInitialTableType => ({
  type: 'empty-initial-table',
  dashboardElementId: dashboardElementId,
  elementData: elementData,
  props: props,
});

export const makeEmptyInitialResourceWidgetRepresentation = (
  dashboardElementId: number,
  elementData: ApiDashboardElement,
  props: WidgetParamsFromName<'empty-initial-resource-widget'>
): EmptyInitialResourceWidgetType => ({
  type: 'empty-initial-resource-widget',
  dashboardElementId: dashboardElementId,
  elementData: elementData,
  props: props,
});

type TextWidgetType = WidgetType & { type: 'text' };

export const makeTextRepresentation = (
  dashboardElementId: number,
  elementData: ApiDashboardElement,
  props: WidgetParamsFromName<'text'>
): TextWidgetType => ({
  type: 'text',
  dashboardElementId,
  elementData,
  props,
});

export const makeTextInputRepresentation = (
  dashboardElementId: number,
  elementData: ApiDashboardElement,
  props: WidgetParamsFromName<'text-input'>
): TextType => ({
  type: 'text-input',
  dashboardElementId,
  elementData,
  props,
});

type TextFromExternaltWidgetType = WidgetType & { type: 'text-from-external' };

export const makeTextFromExternalRepresentation = (
  dashboardElementId: number,
  elementData: ApiDashboardElement,
  props: WidgetParamsFromName<'text-from-external'>
): TextFromExternaltWidgetType => ({
  type: 'text-from-external',
  dashboardElementId,
  elementData,
  props,
});

type GroupWidgetType = WidgetType & { type: 'group' };

export const makeGroupRepresentation = (
  dashboardElementId: number,
  elementData: ApiDashboardElement,
  props: WidgetParamsFromName<'group'>
): GroupWidgetType => ({
  type: 'group',
  dashboardElementId,
  elementData,
  props,
});

type MapWidgetType = WidgetType & { type: 'mapWidget' };

export const makeMapWidgetRepresentation = (
  dashboardElementId: number,
  elementData: ApiDashboardElement,
  props: WidgetParamsFromName<'mapWidget'>
): MapWidgetType => ({
  type: 'mapWidget',
  dashboardElementId,
  elementData,
  props,
});

type ImageWidgetType = WidgetType & { type: 'imageWidget' };

export const makeImageWidgetRepresentation = (
  dashboardElementId: number,
  elementData: ApiDashboardElement,
  props: WidgetParamsFromName<'imageWidget'>
): ImageWidgetType => ({
  type: 'imageWidget',
  dashboardElementId,
  elementData,
  props,
});

export const makeImageGalleryWidgetRepresentation = (
  dashboardElementId: number,
  elementData: ApiDashboardElement,
  props: WidgetParamsFromName<'image-gallery-widget'>
): ImageGalleryWidgetType => ({
  type: 'image-gallery-widget',
  dashboardElementId,
  elementData,
  props,
});

export const makeRemoteWidgetRepresentation = (
  dashboardElementId: number,
  elementData: ApiDashboardElement,
  props: WidgetParamsFromName<'remote-widget'>
): RemoteWidgetType => ({
  type: 'remote-widget',
  dashboardElementId,
  elementData,
  props,
});

export const makeWidgetRepresentation = <T extends WidgetNameType>(
  dashboardElementId: number,
  elementData: ApiDashboardElement,
  type: T,
  props: WidgetParamsFromName<T>
): WidgetType => {
  switch (type) {
    case 'displayer':
      return makeDisplayerRepresentation(
        dashboardElementId,
        elementData,
        props as WidgetParamsFromName<'displayer'>
      );
    case 'empty-initial-plot':
      return makeEmptyInitialPlotRepresentation(
        dashboardElementId,
        elementData,
        props as WidgetParamsFromName<'empty-initial-plot'>
      );
    case 'empty-initial-table':
      return makeEmptyInitialTableRepresentation(
        dashboardElementId,
        elementData,
        props as WidgetParamsFromName<'empty-initial-table'>
      );
    case 'empty-initial-resource-widget':
      return makeEmptyInitialResourceWidgetRepresentation(
        dashboardElementId,
        elementData,
        props as WidgetParamsFromName<'empty-initial-resource-widget'>
      );
    case 'text':
      return makeTextRepresentation(
        dashboardElementId,
        elementData,
        props as WidgetParamsFromName<'text'>
      );
    case 'text-input':
      return makeTextInputRepresentation(
        dashboardElementId,
        elementData,
        props as WidgetParamsFromName<'text-input'>
      );
    case 'text-from-external':
      return makeTextFromExternalRepresentation(
        dashboardElementId,
        elementData,
        props as WidgetParamsFromName<'text-from-external'>
      );
    case 'group':
      return makeGroupRepresentation(
        dashboardElementId,
        elementData,
        props as WidgetParamsFromName<'group'>
      );
    case 'mapWidget':
      return makeMapWidgetRepresentation(
        dashboardElementId,
        elementData,
        props as WidgetParamsFromName<'mapWidget'>
      );
    case 'imageWidget':
      return makeImageWidgetRepresentation(
        dashboardElementId,
        elementData,
        props as WidgetParamsFromName<'imageWidget'>
      );
    case 'remote-widget':
      return makeRemoteWidgetRepresentation(
        dashboardElementId,
        elementData,
        props as WidgetParamsFromName<'remote-widget'>
      );
    case 'image-gallery-widget':
      return makeImageGalleryWidgetRepresentation(
        dashboardElementId,
        elementData,
        props as WidgetParamsFromName<'image-gallery-widget'>
      );
    default:
      throw new Error(`Unknown widget type: ${type}`);
  }
};

type WidgetConfigurationMap = {
  [N in WidgetNameType]: { component: WidgetFromName<N> };
};

export const widget_configurations: WidgetConfigurationMap = {
  displayer: { component: DisplayerWidget },
  'empty-initial-plot': { component: EmptyInitialPlot },
  'empty-initial-table': { component: EmptyInitialTable },
  'empty-initial-resource-widget': { component: EmptyInitialResourceWidget },

  text: { component: TextWidget },
  'text-input': { component: Text },
  'text-from-external': { component: TextFromExternal },

  group: { component: GroupWidget },
  mapWidget: { component: Map },
  imageWidget: { component: ImageWidget },
  'remote-widget': { component: RemoteWidget },
  'image-gallery-widget': { component: ImageGallery },
};
